// src/ui/auth/SignInPage.jsx

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import authService, { OtpRateLimitException } from '../../services/auth/authService';

export const SignInPage = () => {
  const navigate = useNavigate();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isValid, setIsValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const inputRef = useRef(null);

  // ── Focus input on mount ──────────────────────────────────────────────────

  useEffect(() => {
    setTimeout(() => {
      inputRef.current?.focus();
    }, 300);
  }, []);

  // ── Handle phone number change ────────────────────────────────────────────

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    setPhoneNumber(value);
    setIsValid(value.length > 0);
    setErrorMessage(null);
  };

  // ── Go to OTP verification ───────────────────────────────────────────────

  const handleNext = async () => {
    if (!isValid || isLoading) return;

    const fullPhone = `+${phoneNumber.trim()}`;

    setIsLoading(true);
    setErrorMessage(null);

    try {
      const result = await authService.sendFirebaseOtp(fullPhone);

      navigate('/login-otp', {
        state: {
          phoneNumber: fullPhone,
          verificationId: result.verificationId,
          resendToken: result.resendToken,
        },
      });
    } catch (error) {
      let message = 'Something went wrong. Please try again.';
      
      if (error.code === 'invalid-phone-number') {
        message = 'Invalid phone number format.';
      } else if (error.message) {
        message = error.message;
      } else if (error instanceof OtpRateLimitException) {
        message = `Too many attempts. Please wait ${error.formattedRemaining} before trying again.`;
      }
      
      setErrorMessage(message);
      setIsLoading(false);
    }
  };

  // ── Handle Enter key ──────────────────────────────────────────────────────

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleNext();
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-filatom-bg flex items-center justify-center px-6">
      <div className="w-full max-w-md mx-auto">
        {/* Header */}
        <div className="text-center mt-4">
          <h1 className="text-white text-xl font-medium">Log In</h1>
          <p className="text-white/50 text-sm mt-2">
            Enter your Phone number to log in.
          </p>
        </div>

        {/* Phone Input */}
        <div className="mt-7">
          <div
            className="rounded-xl px-4 py-2 flex items-center"
            style={{ backgroundColor: '#23262C' }}
          >
            <span className="text-white text-sm mr-2">+</span>
            <input
              ref={inputRef}
              type="tel"
              value={phoneNumber}
              onChange={handlePhoneChange}
              onKeyPress={handleKeyPress}
              placeholder="Phone number"
              className="flex-1 bg-transparent text-white text-sm outline-none placeholder:text-white/50"
              inputMode="numeric"
              autoComplete="tel"
            />
          </div>
        </div>

        {/* Error Message */}
        {errorMessage && (
          <div className="mt-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
            <p className="text-red-400 text-sm">{errorMessage}</p>
          </div>
        )}

        {/* Spacer */}
        <div className="flex-1 h-8" />

        {/* Next Button - Fixed at bottom */}
        <div className="mt-8 w-full pb-8">
          <button
            onClick={handleNext}
            disabled={!isValid || isLoading}
            className={`w-full py-3 rounded-xl text-white text-sm font-medium transition-all ${
              isValid && !isLoading
                ? 'bg-filatom-primary hover:opacity-90 active:scale-98'
                : 'bg-filatom-card text-gray-500 cursor-not-allowed'
            }`}
          >
            {isLoading ? (
              <div className="flex items-center justify-center gap-2">
                <div className="w-4.5 h-4.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Sending...</span>
              </div>
            ) : (
              'Next'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SignInPage;